import React from 'react';
import { Box, Table, TableBody, TableCell, TableHead, TableRow, Tooltip, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';
import { SegmentedControl } from './controls';

/**
 * Chart card (book §13): a title that states the finding, the period, a
 * source/freshness line, and an accessible table view. Wrap any chart form
 * (BarList, a custom SVG) in it.
 */
export interface ChartCardProps {
  /** States the question or finding: "Sales has the most days out this quarter". */
  title: React.ReactNode;
  /** "Jul 1 – Sep 30, 2026" */
  period?: React.ReactNode;
  /** "Source: approved time-off requests · Updated 9:03 AM ET" */
  source?: React.ReactNode;
  action?: React.ReactNode;
  /** Columns and rows for the "Table" view. Omit only when the chart already is a table. */
  table?: { columns: string[]; rows: React.ReactNode[][]; numericColumns?: number[] };
  legend?: React.ReactNode;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

export const ChartCard: React.FC<ChartCardProps> = ({ title, period, source, action, table, legend, children, sx }) => {
  const [view, setView] = React.useState<'chart' | 'table'>('chart');
  return (
    <Box
      component="section"
      sx={{
        bgcolor: sbg.bg.surface,
        border: `1px solid ${sbg.border.default}`,
        borderRadius: sbg.radius.card,
        p: 2.5,
        display: 'grid',
        gap: 2,
        alignContent: 'start',
        minWidth: 0,
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1.5, flexWrap: 'wrap' }}>
        <Box sx={{ minWidth: 0, flex: '1 1 240px' }}>
          <Typography variant="h4" component="h2" sx={{ color: sbg.text.primary }}>
            {title}
          </Typography>
          {period && (
            <Typography variant="body2" component="p" sx={{ color: sbg.text.secondary, mt: 0.25 }}>
              {period}
            </Typography>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action}
          {table && (
            <SegmentedControl
              size="small"
              aria-label="Chart view"
              value={view}
              onChange={setView}
              options={[
                { value: 'chart', label: 'Chart' },
                { value: 'table', label: 'Table' },
              ]}
            />
          )}
        </Box>
      </Box>
      {legend && view === 'chart' && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>{legend}</Box>}
      {view === 'chart' || !table ? (
        <Box sx={{ minWidth: 0 }}>{children}</Box>
      ) : (
        <Box sx={{ overflow: 'auto', border: `1px solid ${sbg.border.default}`, borderRadius: sbg.radius.lg }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                {table.columns.map((column, index) => (
                  <TableCell key={column} align={table.numericColumns?.includes(index) ? 'right' : 'left'}>
                    {column}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {table.rows.map((row, rowIndex) => (
                <TableRow key={rowIndex}>
                  {row.map((cell, index) => (
                    <TableCell key={index} align={table.numericColumns?.includes(index) ? 'right' : 'left'}>
                      {cell}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      {source && (
        <Typography variant="caption" component="p" sx={{ color: sbg.text.secondary }}>
          {source}
        </Typography>
      )}
    </Box>
  );
};

/** A legend swatch + label. Text wears text tokens, never the series color. */
export const LegendItem: React.FC<{ color: string; label: React.ReactNode; hatched?: boolean }> = ({ color, label, hatched }) => (
  <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.75 }}>
    <Box
      aria-hidden="true"
      sx={{
        width: 10,
        height: 10,
        borderRadius: '3px',
        bgcolor: color,
        ...(hatched ? { background: `repeating-linear-gradient(-30deg, ${color} 0 2px, transparent 2px 4px)`, border: `1px solid ${color}` } : null),
      }}
    />
    <Typography variant="body2" component="span" sx={{ color: sbg.text.secondary }}>
      {label}
    </Typography>
  </Box>
);

export interface BarListItem {
  key: string;
  label: React.ReactNode;
  value: number;
  /** Formatted value shown at the end of the row ("12 days"). */
  display?: React.ReactNode;
  /** Optional secondary caption under the label. */
  caption?: React.ReactNode;
  /** Series color; defaults to chart slot 1 (Blue). Use status roles only for true states. */
  color?: string;
  tooltip?: string;
  onClick?: () => void;
}

/**
 * Sorted horizontal bars for comparing categories (book §13.2): labels on
 * the left, a 4px-rounded bar, the exact value right-aligned in tabular
 * figures. Sort before passing (largest first) unless order carries meaning.
 */
export const BarList: React.FC<{ items: BarListItem[]; max?: number; barHeight?: number; sx?: SxProps<Theme> }> = ({
  items,
  max,
  barHeight = 8,
  sx,
}) => {
  const ceiling = max ?? Math.max(1, ...items.map((item) => item.value));
  return (
    <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0, display: 'grid', gap: 1.5, ...sx }}>
      {items.map((item) => {
        const pct = Math.max(0, Math.min(100, (item.value / ceiling) * 100));
        const row = (
          <Box
            component={item.onClick ? 'button' : 'div'}
            type={item.onClick ? 'button' : undefined}
            onClick={item.onClick}
            sx={{
              all: 'unset',
              boxSizing: 'border-box',
              width: '100%',
              display: 'grid',
              gridTemplateColumns: 'minmax(96px, 32%) 1fr auto',
              alignItems: 'center',
              gap: 1.5,
              borderRadius: sbg.radius.md,
              ...(item.onClick
                ? { cursor: 'pointer', '&:hover': { bgcolor: sbg.bg.hover }, '&:focus-visible': { outline: `2px solid ${sbg.focus.ring}`, outlineOffset: 2 } }
                : null),
            }}
          >
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body1" component="div" noWrap sx={{ color: sbg.text.primary }}>
                {item.label}
              </Typography>
              {item.caption && (
                <Typography variant="caption" component="div" noWrap sx={{ color: sbg.text.secondary }}>
                  {item.caption}
                </Typography>
              )}
            </Box>
            <Box sx={{ height: barHeight, bgcolor: 'transparent', borderRadius: '0 4px 4px 0', minWidth: 0 }}>
              <Box
                sx={{
                  height: '100%',
                  width: `${pct}%`,
                  minWidth: item.value > 0 ? 2 : 0,
                  bgcolor: item.color ?? sbg.chart.series[0],
                  borderRadius: '0 4px 4px 0',
                  transition: `width ${sbg.duration.slow} ${sbg.ease.enter}`,
                }}
              />
            </Box>
            <Typography variant="labelSm" component="div" sx={{ color: sbg.text.primary, fontVariantNumeric: 'tabular-nums', textAlign: 'right', minWidth: 40 }}>
              {item.display ?? item.value}
            </Typography>
          </Box>
        );
        return (
          <Box component="li" key={item.key}>
            {item.tooltip ? <Tooltip title={item.tooltip} placement="top-start">{row}</Tooltip> : row}
          </Box>
        );
      })}
    </Box>
  );
};

/** One stacked bar for parts of a whole (≤3 parts, book §13.2). */
export const StackedBar: React.FC<{
  parts: { key: string; label: string; value: number; color: string }[];
  height?: number;
  'aria-label'?: string;
}> = ({ parts, height = 12, ...rest }) => {
  const total = parts.reduce((sum, part) => sum + Math.max(0, part.value), 0) || 1;
  return (
    <Box
      role="img"
      aria-label={rest['aria-label'] ?? parts.map((p) => `${p.label}: ${p.value}`).join(', ')}
      sx={{ display: 'flex', gap: '2px', height, borderRadius: '4px', overflow: 'hidden', bgcolor: sbg.bg.track }}
    >
      {parts
        .filter((part) => part.value > 0)
        .map((part) => (
          <Tooltip key={part.key} title={`${part.label}: ${part.value}`}>
            <Box sx={{ width: `${(part.value / total) * 100}%`, bgcolor: part.color, minWidth: 2 }} />
          </Tooltip>
        ))}
    </Box>
  );
};
